import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StatusFormData } from "../components/add-edit-stage/components/AddStatusAndSubStatus";
import { transformStatusesForBackend } from "../utils/transformStatusesForBackend";

/* ================= TYPES ================= */

export interface RollbackObj {
  parentId?: number;
  parentName?: string;
  subId?: number;
  subName?: string;
}

export interface AddStage {
  name: string;
  description: string;

  isTracks: boolean;
  isChoices: boolean;
  specificStatuses: boolean;
  collaborator: boolean;
  isAnchorPoint: boolean;
  anchorName: string;
  verifyNewThisPeriod: boolean;
  verifyExcludeRejected: boolean;
  verifyExcludeRollback: boolean;
  verifyPreferActive: boolean;
  verifyTieBreakEarliest: boolean;
  verifyPreferLatestStage: boolean;
  verifyCreditOnce: boolean;
  verifyPeriodGrain: "month" | "week";

  maxChoices: number;
  isAllowCustomName: boolean;
  isAutoPauseOther: boolean;

  isLeadOwnerAccess: boolean;
  isTakeOver: boolean;
  isAllowCollabRemoval: boolean;

  // ✅ Status Logic
  initialStatusCardsData: StatusFormData;
  qualifyingStatus: string;
  defaultQualifyingSubStatus: any;
  rejectionStatus: string;
  /** Sub-status-level rejections as `"statusName::subName"` keys. */
  rejectionSubStatuses: string[];
  defaultRollbackStatusObj: RollbackObj;

  isShowInLead: boolean;
  isShowInRoute: boolean;

  icon: string;
}

/* ================= INITIAL STATE ================= */

const initialState: AddStage = {
  name: "",
  description: "",

  isTracks: false,
  isChoices: false,
  specificStatuses: false,
  collaborator: false,
  isAnchorPoint: false,
  anchorName: "Verified",
  verifyNewThisPeriod: true,
  verifyExcludeRejected: true,
  verifyExcludeRollback: false,
  verifyPreferActive: true,
  verifyTieBreakEarliest: true,
  verifyPreferLatestStage: false,
  verifyCreditOnce: false,
  verifyPeriodGrain: "month",

  maxChoices: 8,
  isAllowCustomName: true,
  isAutoPauseOther: false,

  isLeadOwnerAccess: true,
  isTakeOver: true,
  isAllowCollabRemoval: true,

  // ✅ Status defaults (EMPTY – ADD MODE)
  qualifyingStatus: "",
  defaultQualifyingSubStatus: {},
  rejectionStatus: "",
  rejectionSubStatuses: [],
  defaultRollbackStatusObj: {},

  initialStatusCardsData: [
    {
      id: Date.now(),
      statusName: "",
      subStatuses: [{ id: Date.now() + 1, name: "" }],
    },
  ],

  isShowInLead: false,
  isShowInRoute: false,

  icon: "Home",
};

/* ================= SLICE ================= */

const addStageSlice = createSlice({
  name: "addStage",
  initialState,
  reducers: {
    /* -------- BASIC FIELDS -------- */

    setAddStageName(state, action: PayloadAction<string>) {
      state.name = action.payload;
    },

    setAddStageDescription(state, action: PayloadAction<string>) {
      state.description = action.payload;
    },

    setAddStageIsTracks(state, action: PayloadAction<boolean>) {
      state.isTracks = action.payload;
    },

    setAddStageIsChoices(state, action: PayloadAction<boolean>) {
      state.isChoices = action.payload;
    },

    setAddStageSpecificStatuses(state, action: PayloadAction<boolean>) {
      state.specificStatuses = action.payload;
    },

    setAddStageCollaborator(state, action: PayloadAction<boolean>) {
      state.collaborator = action.payload;
    },

    setAddStageAnchorPoint(state, action: PayloadAction<boolean>) {
      state.isAnchorPoint = action.payload;
    },

    setAddStageAnchorName(state, action: PayloadAction<string>) {
      state.anchorName = action.payload;
    },

    setAddStageVerifyCondition(
      state,
      action: PayloadAction<{
        key:
          | "verifyNewThisPeriod"
          | "verifyExcludeRejected"
          | "verifyExcludeRollback"
          | "verifyPreferActive"
          | "verifyTieBreakEarliest"
          | "verifyPreferLatestStage"
          | "verifyCreditOnce";
        value: boolean;
      }>
    ) {
      state[action.payload.key] = action.payload.value;
    },

    setAddStagePeriodGrain(state, action: PayloadAction<"month" | "week">) {
      state.verifyPeriodGrain = action.payload;
    },

    setMaxChoices(state, action: PayloadAction<number>) {
      state.maxChoices = action.payload;
    },

    setIsAllowCustomName(state, action: PayloadAction<boolean>) {
      state.isAllowCustomName = action.payload;
    },

    setIsAutoPauseOther(state, action: PayloadAction<boolean>) {
      state.isAutoPauseOther = action.payload;
    },

    setAddStageIsLeadOwner(state, action: PayloadAction<boolean>) {
      state.isLeadOwnerAccess = action.payload;
    },

    setAddStageIsTakeOver(state, action: PayloadAction<boolean>) {
      state.isTakeOver = action.payload;
    },

    setAddIsAllowCollabRemoval(state, action: PayloadAction<boolean>) {
      state.isAllowCollabRemoval = action.payload;
    },

    setAddStageIsShowInLead(state, action: PayloadAction<boolean>) {
      state.isShowInLead = action.payload;
    },

    setAddStageIsShowInRightSide(state, action: PayloadAction<boolean>) {
      state.isShowInRoute = action.payload;
    },

    setAddStageIcon(state, action: PayloadAction<string>) {
      state.icon = action.payload;
    },

    /* -------- STATUS LOGIC -------- */

    setAddInitialStatusCardsData(state, action) {
      state.initialStatusCardsData = action.payload;
    },

    setAddQualifyingStatus(state, action: PayloadAction<string>) {
      state.qualifyingStatus = action.payload;
    },

    setAddDefaultQualifyingSubStatus(state, action: PayloadAction<any>) {
      state.defaultQualifyingSubStatus = action.payload;
    },

    setAddRejectionStatus(state, action: PayloadAction<string>) {
      state.rejectionStatus = action.payload;
    },

    setAddRejectionSubStatuses(state, action: PayloadAction<string[]>) {
      state.rejectionSubStatuses = action.payload;
    },

    setAddDefaultRollbackStatusObj(state, action: PayloadAction<RollbackObj>) {
      state.defaultRollbackStatusObj = action.payload;
    },

    /* -------- RESET -------- */

    resetAddStage() {
      return initialState;
    },
  },
});

export const selectAddStageBackendVariables = (state: any) => {
  const s = state?.addstage;

  if (!s) {
    return {
      name: "",
      description: "",
      isTracks: false,
      isChild: false,
      maxChilds: 0,
      isSpecific: false,
      isCollaborator: false,
      isAnchorPoint: false,
      anchorName: null,
      verifyNewThisPeriod: true,
      verifyExcludeRejected: true,
      verifyExcludeRollback: false,
      verifyPreferActive: true,
      verifyTieBreakEarliest: true,
      verifyPreferLatestStage: false,
      verifyCreditOnce: false,
      verifyPeriodGrain: "month",
      isAllowCustomName: false,
      isAutoPauseOtherChoices: false,
      isLeadOwner: false,
      isLeadTakeOver: false,
      isAllowCollaboratorRemoval: false,
      isShowInLeadProfile: false,
      isShowInSideRoute: false,
      iconName: "",
      statuses: [],
      initialStatusCardsData: [
        {
          id: 1,
          statusName: "",
          subStatuses: [{ id: 0, name: "" }],
        },
      ],
    };
  }

  return {
    name: s.name ?? "",
    description: s.description ?? "",

    isTracks: !!s.isTracks,
    isChild: !!s.isChoices,
    maxChilds: s.maxChoices ?? 0,
    isSpecific: !!s.specificStatuses,
    isCollaborator: !!s.collaborator,
    isAnchorPoint: !!s.isAnchorPoint,
    anchorName: s.anchorName ?? null,
    verifyNewThisPeriod: s.verifyNewThisPeriod !== false,
    verifyExcludeRejected: s.verifyExcludeRejected !== false,
    verifyExcludeRollback: s.verifyExcludeRollback === true,
    verifyPreferActive: s.verifyPreferActive !== false,
    verifyTieBreakEarliest: s.verifyTieBreakEarliest !== false,
    verifyPreferLatestStage: s.verifyPreferLatestStage === true,
    verifyCreditOnce: s.verifyCreditOnce === true,
    verifyPeriodGrain: s.verifyPeriodGrain === "week" ? "week" : "month",
    isAllowCustomName: !!s.isAllowCustomName,
    isAutoPauseOtherChoices: !!s.isAutoPauseOther,
    isLeadOwner: !!s.isLeadOwnerAccess,
    isLeadTakeOver: !!s.isTakeOver,
    isAllowCollaboratorRemoval: !!s.isAllowCollabRemoval,
    isShowInLeadProfile: !!s.isShowInLead,
    isShowInSideRoute: !!s.isShowInRoute,
    iconName: s.icon ?? "",

    statuses: transformStatusesForBackend(
      s.initialStatusCardsData,
      s.qualifyingStatus,
      s.defaultQualifyingSubStatus,
      s.rejectionStatus ? [s.rejectionStatus] : [],
      s.defaultRollbackStatusObj,
      s.rejectionSubStatuses
    ),
  };
};

export const {
  setAddStageName,
  setAddStageDescription,
  setAddStageIsTracks,
  setAddStageIsChoices,
  setAddStageSpecificStatuses,
  setAddStageCollaborator,
  setAddStageAnchorPoint,
  setAddStageAnchorName,
  setAddStageVerifyCondition,
  setAddStagePeriodGrain,
  setMaxChoices,
  setIsAllowCustomName,
  setIsAutoPauseOther,
  setAddStageIsLeadOwner,
  setAddStageIsTakeOver,
  setAddIsAllowCollabRemoval,
  setAddStageIsShowInLead,
  setAddStageIsShowInRightSide,
  setAddStageIcon,

  setAddInitialStatusCardsData,
  setAddQualifyingStatus,
  setAddDefaultQualifyingSubStatus,
  setAddRejectionStatus,
  setAddRejectionSubStatuses,
  setAddDefaultRollbackStatusObj,

  resetAddStage,
} = addStageSlice.actions;

export default addStageSlice.reducer;
